import { Chip, Container, Stack, Typography } from "@mui/material";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useClinician } from "src/context/SessionProvider/ClinicianProvider";
import QuestionAnswerBlock from "src/components/QuestionAnswerBlock";
import { useGetConversationQuery } from "src/api/services/conversationsApi";
import HistoricalBLock from "./components/HistoricalBlock";
import { useSearchParams } from "react-router-dom";
import { useQueryParams } from "src/hooks/useQueryParams";
import { skipToken } from "@reduxjs/toolkit/query";
import { parseAiConversationId } from "src/utils/parseAiConversationId";
import { useAppDispatch } from "src/api/hooks";
import { conversationThreadsApi } from "src/api/services/conversationThreadsApi";
import { TAGS } from "src/api/constants";
import ChatInput from "./components/ChatInput";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import { Image } from "@mui/icons-material";
import { AiInteractionFile } from "src/api/types";

type ChatEntry =
  | {
      kind: "historical";
      question: string;
      answer: string;
      createdAt: string;
      id: string;
      files?: AiInteractionFile[];
    }
  | {
      kind: "live";
      id: string;
      question: string;
      file: File | null;
    };

const AskVida = () => {
  const dispatch = useAppDispatch();
  const containerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const stickToBottom = useRef(true);
  const [searchParams] = useSearchParams();
  const { setParams } = useQueryParams();
  const firstName = useClinician((c) => c.first_name);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [entries, setEntries] = useState<ChatEntry[]>([]);
  const [value, setValue] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const onScroll = useCallback(() => {
    const el = containerRef.current!;
    // within 80px of bottom = "following the stream"
    stickToBottom.current =
      el.scrollHeight - el.scrollTop - el.clientHeight < 80;
  }, []);

  const selectedConversation = useMemo(
    () => searchParams.get("conversation"),
    [searchParams],
  );

  const { data } = useGetConversationQuery(selectedConversation ?? skipToken);

  const scrollToBottom = useCallback((behavior: ScrollBehavior = "auto") => {
    const el = containerRef.current;
    if (el) el.scrollTo({ top: el.scrollHeight, behavior });
  }, []);

  const handleRemoveFile = useCallback(() => {
    setFile(null);
  }, []);

  const handleSubmit = useCallback(() => {
    const id = window.crypto.randomUUID();

    setEntries((prev) =>
      prev.concat({
        kind: "live" as const,
        id,
        question: value,
        file,
      }),
    );
    setValue("");
    setFile(null);
    setIsLoading(true);
    requestAnimationFrame(() => scrollToBottom("smooth"));
  }, [file, scrollToBottom, value]);

  const onSuccess = useCallback(() => {
    setIsLoading(false);
    dispatch(
      conversationThreadsApi.util.invalidateTags([TAGS.CONVERSATION_THREADS]),
    );
  }, [dispatch]);

  useEffect(() => {
    if (conversationId) return;

    if (selectedConversation != null) {
      if (!data) return;

      const { uuid } = parseAiConversationId(selectedConversation);

      setConversationId(uuid);

      setEntries(
        data.messages.map((m) => ({
          kind: "historical",
          answer: m.answer,
          question: m.question,
          createdAt: m.createdAt,
          id: m.id,
          files: m.files,
        })),
      );
    } else {
      setConversationId(window.crypto.randomUUID());
    }
  }, [conversationId, data, file, selectedConversation, setParams]);

  useEffect(() => {
    const content = contentRef.current;
    if (!content) return;
    const ro = new ResizeObserver(() => {
      if (stickToBottom.current) scrollToBottom("auto");
    });
    ro.observe(content);
    return () => ro.disconnect();
  }, [scrollToBottom]);

  return (
    <Container maxWidth="md" sx={{ height: "100%" }}>
      <Stack alignItems="center" justifyItems="center" height={"100%"}>
        <Stack
          ref={containerRef}
          onScroll={onScroll}
          maxHeight={window.innerHeight - 32 - 90}
          minHeight={0}
          overflow={"auto"}
          width={"100%"}
          alignSelf={"baseline"}
          p={2}
          sx={{
            wordWrap: "break-word",
            borderRadius: "10px",
          }}
        >
          <Stack ref={contentRef}>
            {entries.map((entry) =>
              entry.kind === "historical" ? (
                <HistoricalBLock
                  key={entry.id}
                  agent="vida"
                  answer={entry.answer}
                  question={entry.question}
                  files={entry.files}
                />
              ) : (
                <QuestionAnswerBlock
                  onSuccess={onSuccess}
                  question={{
                    id: entry.id,
                    text: entry.question,
                    file: entry.file,
                  }}
                  conversationId={conversationId}
                  key={entry.id}
                  agent="vida"
                />
              ),
            )}
          </Stack>
        </Stack>
        <Stack m={"auto"} gap={3} width={"100%"}>
          {entries.length < 1 ? (
            <Typography variant="h3">
              What is on your mind {firstName}?
            </Typography>
          ) : null}
          <Stack gap={1}>
            {file != null ? (
              <Stack direction={"row"}>
                <Chip
                  label={file.name}
                  onDelete={handleRemoveFile}
                  icon={
                    file.type === "application/pdf" ? (
                      <PictureAsPdfIcon />
                    ) : (
                      <Image />
                    )
                  }
                />
              </Stack>
            ) : null}
            <ChatInput
              value={value}
              setValue={setValue}
              handleSubmit={handleSubmit}
              isLoading={isLoading}
              setFile={setFile}
            />
          </Stack>
        </Stack>
      </Stack>
    </Container>
  );
};

export default AskVida;
